import { Link, useLocation, useNavigate } from "react-router-dom";
import { Users, UserCircle, Repeat, Settings, LogOut, HomeIcon, Phone, Warehouse,
  HardDrive, Package, Store, Boxes, MoveRight, Puzzle, Bell
} from "lucide-react";
import { useMemo, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../services/api";
import { endpoints } from "../services/endpoints";

const modules = [
  // ADMIN
  { labelKey: "sidebar.home", path: "/home/admin", icon: <HomeIcon size={18} />, roles: ["ADMIN"] },
  { labelKey: "sidebar.users", path: "/dashboardusersadmin", icon: <Users size={18} />, roles: ["ADMIN"] },
  { labelKey: "sidebar.devices", path: "/rfidsadmin", icon: <Phone size={18} />, roles: ["ADMIN"] },
  { labelKey: "sidebar.companies", path: "/dashboardempresasadmin", icon: <Repeat size={18} />, roles: ["ADMIN"] },
  { labelKey: "sidebar.apk", path: "/apk", icon: <Phone size={18} />, roles: ["ADMIN"] },

  // DIRECTOR
  { labelKey: "sidebar.home", path: "/home/director", icon: <HomeIcon size={18} />, roles: ["DIRECTOR"] },
  { labelKey: "sidebar.warehouses", path: "/warehouses", icon: <Warehouse size={18} />, roles: ["DIRECTOR"] },
  { labelKey: "sidebar.devices", path: "/devices", icon: <HardDrive size={18} />, roles: ["DIRECTOR"] },
  { labelKey: "sidebar.inventory", path: "/inventory", icon: <Boxes size={18} />, roles: ["DIRECTOR"] },
  { labelKey: "sidebar.movements", path: "/movement", icon: <MoveRight size={18} />, roles: ["DIRECTOR"] },
  /*{ labelKey: "sidebar.clientModules", path: "/client-modules", icon: <Puzzle size={18} />, roles: ["DIRECTOR"] },*/
  { labelKey: "sidebar.users", path: "/operators", icon: <Users size={18} />, roles: ["DIRECTOR"] },

  // SUPERVISOR
  { labelKey: "sidebar.home", path: "/home/supervisor", icon: <HomeIcon size={18} />, roles: ["SUPERVISOR"] },
  { labelKey: "sidebar.devices", path: "/devices", icon: <HardDrive size={18} />, roles: ["SUPERVISOR"] },

  // Supervisor y director
  { labelKey: "sidebar.products", path: "/products", icon: <Package size={18} />, roles: ["DIRECTOR", "SUPERVISOR"] },

  // director
  { labelKey: "sidebar.inventory", path: "/inventory", icon: <Boxes size={18} />, roles: ["SUPERVISOR"] },
  { labelKey: "sidebar.movements", path: "/movement", icon: <MoveRight size={18} />, roles: ["SUPERVISOR"] },

  // Director y supervisor
  { labelKey: "sidebar.suppliers", path: "/supplier", icon: <Puzzle size={18} />, roles: ["DIRECTOR", "SUPERVISOR"] },
  { labelKey: "sidebar.stores", path: "/stores", icon: <Store size={18} />, roles: ["DIRECTOR", "SUPERVISOR"] },
  { labelKey: "sidebar.externalClients", path: "/external-clients", icon: <UserCircle size={18} />, roles: ["DIRECTOR", "SUPERVISOR"] },
  
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const [pendingCount, setPendingCount] = useState(0);

  const storedUser = localStorage.getItem("user");
  let user = null;
  try { user = storedUser ? JSON.parse(storedUser) : null; } catch {}

  // En que empresa esta trabajando quien mira. Viene del login, que lo
  // deja guardado junto al resto del usuario.
  const companyName = user?.company_name || null;
  const companyLogo = user?.company_logo_url || null;

  const username =
    user?.full_name ||
    user?.name ||
    user?.username ||
    user?.user_name ||
    user?.dni ||
    user?.email?.split("@")[0] ||
    t("sidebar.noUser", "Sin usuario");
  const role = useMemo(() => {
    const raw = user?.role ?? user?.typeUser ?? user?.type ?? user?.rol ?? null;
    return raw ? String(raw).toUpperCase() : null;
  }, [user]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const filteredModules = useMemo(
    () => modules.filter((m) => !m.roles || m.roles.includes(role)),
    [role]
  );

  const handleNavClick = () => setOpen(false);

  const fetchPendingAlerts = useCallback(async () => {
    try {
      if (role === "SUPERVISOR") {
        const res = await api.get(endpoints.alerts.byWarehouse);
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const unresolvedCount = data.filter(
          (alert) => String(alert?.status ?? "").toUpperCase() !== "RESUELTA"
        ).length;
        setPendingCount(unresolvedCount);
        return;
      }

      const res = await api.get(endpoints.alerts.summary);
      setPendingCount(res.data?.total ?? 0);
    } catch (err) {
      console.error("Error cargando resumen de alertas:", err);
      setPendingCount(0);
    }
  }, [role]);

  useEffect(() => {
    fetchPendingAlerts();

    const interval = setInterval(fetchPendingAlerts, 60000);
    return () => clearInterval(interval);
  }, [fetchPendingAlerts]);

  return (
    <>
      {/* Botón hamburguesa */}
      <button
        aria-label={open ? t("sidebar.closeMenu") : t("sidebar.openMenu")}
        onClick={() => setOpen((o) => !o)}
        className="lg:hidden fixed top-4 left-4 z-50 rounded-md p-2 bg-white/90 shadow"
      >
      </button>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setOpen(false)} />
      )}

      <aside className={`fixed left-0 top-0 z-50 w-64 bg-[#01839C] shadow-md p-4 transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        style={{ height: "100dvh", paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="flex h-full flex-col mt-4">
          <div className="mb-6 flex items-center justify-center gap-3 shrink-0">
            <img
              src="/img/sysventory.png"
              alt="Sysventory"
              className="h-15 w-auto object-contain"
            />
          </div>

          {/* En que empresa estamos.
              Sysdil y Dealsys comparten plataforma, pantallas y menus: lo
              unico que cambia son los datos. Una salida hecha en la empresa
              equivocada no da error, descuadra los dos inventarios y no se
              descubre hasta el recuento. Esto es la pista de donde estas.

              Sobre tarjeta blanca porque la barra es oscura y cada logo
              viene con sus colores. Y si la imagen no carga, se esconde y
              queda el nombre: un hueco roto seria peor que nada. */}
          {(companyLogo || companyName) && (
            <div className="mb-5 flex shrink-0 flex-col items-center gap-1">
              {companyLogo && (
                <img
                  src={companyLogo}
                  alt={companyName || "Empresa"}
                  className="h-14 w-auto max-w-[190px] object-contain"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              )}
              {companyName && (
                <span className="text-xs font-semibold tracking-wide text-white/90">
                  {companyName}
                </span>
              )}
            </div>
          )}

          <nav className="flex-1 overflow-y-auto pr-1 -mr-1">
            <div className="flex flex-col gap-2">
              {filteredModules.map(({ labelKey, path, icon }) => {
                const active = location.pathname === path;
                return (
                  <Link
                    key={path}
                    to={path}
                    onClick={handleNavClick}
                    className={[
                      "flex items-center gap-2 text-m px-3 py-2 rounded-md transition-colors",
                      active
                        ? "bg-white text-[#01839C] font-semibold shadow"
                        : "text-white hover:bg-white hover:text-[#01839C]",
                    ].join(" ")}
                  >
                    {icon}
                    <span>{t(labelKey)}</span>
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* 🔔 NOTIFICACIONES –– AÑADIDO */}
          <div className="px-3 mb-4">
            <Link
              to="/home/director"
              onClick={handleNavClick}
              className="flex items-center gap-2 text-white hover:text-[#d9f7ff] transition"
            >
              <div className="relative">
                <Bell size={20} />
                {pendingCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                    {pendingCount}
                  </span>
                )}
              </div>
              <span>{t("sidebar.notifications", "Notificaciones")}</span>
            </Link>
          </div>

          {/* Footer con safe-area + 2px extra */}
          <div
            className="border-t border-white/30 pt-4 mt-4 shrink-0 bg-[#01839C]"
            style={{
              // añade 2px además del safe area inferior (si no hay safe area, quedan 2px)
              paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 2px)",
            }}
          >
            {/**Solucionar nombre / dni usuario */}
            <p className="text-white px-3 mb-2">
              {t("sidebar.userLabel", "Usuario")}: {username}
            </p>
            <Link
              to="/configuration"
              onClick={handleNavClick}
              className={[
                "flex items-center gap-2 px-3 py-2 mb-2 rounded-md transition",
                location.pathname === "/configuration"
                  ? "bg-white text-[#01839C] font-semibold shadow"
                  : "text-white hover:bg-white hover:text-[#01839C]",
              ].join(" ")}
            >
              <Settings size={18} />
              <span>{t("sidebar.settings")}</span>
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md transition-colors bg-transparent text-white hover:bg-red-500"
            >
              <LogOut size={18} />
              <span>{t("sidebar.logout")}</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
